import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, Image, GripVertical } from 'lucide-react';
import { getAllPartners, createPartner, updatePartner, deletePartner } from '../../lib/firebaseService';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

export default function AdminPartners() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    website_url: '',
    order: 0
  });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    loadPartners();
  }, [user, navigate]);

  const loadPartners = async () => {
    try {
      const data = await getAllPartners();
      setPartners(data);
    } catch (error) {
      console.error('Tərəfdaşlar yüklənə bilmədi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.logo_url) {
      toast.error('Ad və logo URL daxil edin');
      return;
    }

    setSaving(true);
    try {
      const partnerData = {
        name: formData.name,
        logo_url: formData.logo_url,
        website_url: formData.website_url || '',
        order: parseInt(formData.order) || partners.length
      };

      if (editingPartner) {
        await updatePartner(editingPartner.id, partnerData);
        toast.success('Tərəfdaş yeniləndi');
      } else {
        await createPartner(partnerData);
        toast.success('Tərəfdaş əlavə edildi');
      }

      setDialogOpen(false);
      resetForm();
      loadPartners();
    } catch (error) {
      console.error('Xəta:', error);
      toast.error('Əməliyyat uğursuz oldu');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name || '',
      logo_url: partner.logo_url || '',
      website_url: partner.website_url || '',
      order: partner.order || 0
    });
    setDialogOpen(true);
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm('Tərəfdaşı silmək istədiyinizə əminsiniz?')) return;
    
    try {
      await deletePartner(partnerId);
      toast.success('Tərəfdaş silindi');
      loadPartners();
    } catch (error) {
      toast.error('Əməliyyat uğursuz oldu');
    }
  };

  const resetForm = () => {
    setEditingPartner(null);
    setFormData({
      name: '',
      logo_url: '',
      website_url: '',
      order: partners.length
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-partners-page">
      <header className="bg-white border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
              </Link>
              <h1 className="text-2xl font-bold">Tərəfdaşlarımız</h1>
            </div>
            <Button onClick={openNewDialog} data-testid="add-partner-button">
              <Plus className="w-4 h-4 mr-2" />
              Yeni Tərəfdaş
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {loading ? (
          <p>Yüklənir...</p>
        ) : partners.length === 0 ? (
          <div className="bg-white rounded-xl border border-border p-12 text-center">
            <Image className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">Heç bir tərəfdaş yoxdur</p>
            <Button onClick={openNewDialog}>İlk tərəfdaşı əlavə et</Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {partners.map((partner) => (
              <div
                key={partner.id}
                className="bg-white rounded-xl border border-border p-6 hover:shadow-lg transition-shadow"
                data-testid={`partner-card-${partner.id}`}
              >
                <div className="flex items-center gap-2 mb-4 text-xs text-muted-foreground">
                  <GripVertical className="w-4 h-4" />
                  <span>Sıra: {partner.order}</span>
                </div>
                
                <div className="aspect-video bg-slate-50 rounded-lg flex items-center justify-center mb-4 p-4">
                  {partner.logo_url ? (
                    <img
                      src={partner.logo_url}
                      alt={partner.name}
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <Image className="w-12 h-12 text-muted-foreground" />
                  )}
                </div>
                
                <h3 className="font-semibold text-lg mb-2">{partner.name}</h3>
                
                {partner.website_url && (
                  <a
                    href={partner.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline block mb-4 truncate"
                  >
                    {partner.website_url}
                  </a>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleEdit(partner)}
                    data-testid={`edit-partner-${partner.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Redaktə
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(partner.id)}
                    data-testid={`delete-partner-${partner.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPartner ? 'Tərəfdaşı Redaktə Et' : 'Yeni Tərəfdaş'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="partner-form">
            <div>
              <Label htmlFor="name">Tərəfdaş Adı *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Şirkət adı"
                required
                data-testid="partner-name-input"
              />
            </div>

            <div>
              <Label htmlFor="logo_url">Logo URL *</Label>
              <Input
                id="logo_url"
                type="url"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                placeholder="https://example.com/logo.png"
                required
                data-testid="partner-logo-input"
              />
              {formData.logo_url && (
                <div className="mt-2 p-4 bg-slate-50 rounded-lg">
                  <img
                    src={formData.logo_url}
                    alt="Logo önizləmə"
                    className="max-h-20 mx-auto object-contain"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="website_url">Veb Sayt URL</Label>
              <Input
                id="website_url"
                type="url"
                value={formData.website_url}
                onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                placeholder="https://example.com"
                data-testid="partner-website-input"
              />
            </div>

            <div>
              <Label htmlFor="order">Sıra Nömrəsi</Label>
              <Input
                id="order"
                type="number"
                min="0"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                data-testid="partner-order-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Kiçik rəqəm = öndə göstərilir</p>
            </div>

            <Button type="submit" className="w-full" disabled={saving} data-testid="save-partner-button">
              {saving ? 'Saxlanılır...' : (editingPartner ? 'Yenilə' : 'Əlavə et')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
